import React, { useEffect, useMemo, useState } from 'react';
import './SpawnTab.scss';

// "Spawns" tab of the admin panel: the zones where wild creatures live (create one, choose what lives there,
// how many, how long a killed group stays dead, what the player is told on entering), and the settings of the
// dungeons. Everything goes through the panel's own messages (adminMenuService.ts relays them to
// spawnZoneSystem.ts and dungeonSystem.ts) and comes back as DOM events the client forwards, same pattern as
// the Placement tab. The server validates every value again.

interface Entry {
  formId: number;
  name: string;
  weight: number;
  minGroup: number;
  maxGroup: number;
  hostile: boolean;
}

interface MobResult {
  id: number;
  n: string;
  edid: string;
  c: string;
  mod: string;
}

interface MobResultsDetail {
  ready: boolean;
  total: number;
  counts: Record<string, number>;
  results: MobResult[];
}

interface Category {
  id: string;
  label: string;
}

interface ZoneInfo {
  id: string;
  name: string;
  enabled: boolean;
  announce: boolean;
  announceText: string;
  maxAlive: number;
  respawnMinutes: number;
  useVanillaPoints: boolean;
  entries: Entry[];
  shape: 'circle' | 'poly';
  radiusMeters: number | null;
  vertexCount: number | null;
  areaKm2: number;
  world: string;
  pointsTotal: number;
  pointsManual: number;
  pointsGame: number;
  manualPoints: number[][];
  alive: number;
  waiting: number;
}

interface Limits {
  maxAlive: number;
  maxRespawnMinutes: number;
  maxEntries: number;
  maxNameLength: number;
  maxAnnounceLength: number;
  minCircleMeters: number;
  maxManualPoints: number;
}

interface StateDetail {
  zones: ZoneInfo[];
  categories: Category[];
  limits: Limits;
  defaultAnnounceText: string;
  totalAlive: number;
}

interface DungeonInfo {
  id: string;
  name: string;
  cellOrWorldDesc: string[];
  excludedCount: number;
  zonesCount: number;
  sessionTimeLimitMinutes: number;
  resetDelayMinutes: number;
  phase: string;
  lootContainers: Array<{ refrId: number; entryCount: number }>;
}

interface Draft {
  name: string;
  enabled: boolean;
  announce: boolean;
  announceText: string;
  maxAlive: number;
  respawnMinutes: number;
  useVanillaPoints: boolean;
  entries: Entry[];
}

const send = (payload: Record<string, unknown>): void => {
  try {
    (window as any).skyrimPlatform.sendMessage(JSON.stringify(payload));
  } catch (e) {
    // Running outside the game (e.g. Storybook) - log instead.
    // eslint-disable-next-line no-console
    console.log('spawnTab sendMessage', payload);
  }
};

const WEIGHTS: Array<{ value: number; label: string }> = [
  { value: 1, label: 'Rare' },
  { value: 3, label: 'Courant' },
  { value: 6, label: 'Fréquent' },
];

const RESPAWN_PRESETS = [5, 15, 30, 60, 120, 360, 1440];

const PHASE_LABEL: Record<string, string> = {
  idle: 'Au repos',
  active: 'En cours',
  cleared: 'Terminé',
  timedout: 'Temps écoulé',
  resetting: 'Réinitialisation',
};

const humanMinutes = (m: number): string => {
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h < 24) return r ? `${h} h ${r} min` : `${h} h`;
  const d = Math.floor(h / 24);
  const hh = h % 24;
  return hh ? `${d} j ${hh} h` : `${d} j`;
};

const toDraft = (z: ZoneInfo): Draft => ({
  name: z.name,
  enabled: z.enabled,
  announce: z.announce,
  announceText: z.announceText,
  maxAlive: z.maxAlive,
  respawnMinutes: z.respawnMinutes,
  useVanillaPoints: z.useVanillaPoints,
  entries: z.entries.map((e) => ({ ...e })),
});

const sameDraft = (a: Draft, b: Draft): boolean => JSON.stringify(a) === JSON.stringify(b);

const clampNum = (v: string, min: number, max: number, fallback: number): number => {
  const n = Math.round(Number(v));
  if (!isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, n));
};

export const SpawnTab: React.FC = () => {
  const [view, setView] = useState<'zones' | 'dungeons'>('zones');
  const [state, setState] = useState<StateDetail | null>(null);
  const [dungeons, setDungeons] = useState<DungeonInfo[] | null>(null);
  const [selected, setSelected] = useState<string | 'new' | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [newName, setNewName] = useState('');
  const [newRadius, setNewRadius] = useState(300);
  const [mobs, setMobs] = useState<MobResultsDetail | null>(null);
  const [picking, setPicking] = useState(false);
  const [mobQuery, setMobQuery] = useState('');
  const [mobCategory, setMobCategory] = useState('animal');

  useEffect(() => {
    const onState = (e: Event) => setState((e as CustomEvent).detail as StateDetail);
    const onDungeons = (e: Event) => setDungeons(((e as CustomEvent).detail as { dungeons: DungeonInfo[] }).dungeons);
    const onMobs = (e: Event) => setMobs((e as CustomEvent).detail as MobResultsDetail);
    window.addEventListener('skymp5-client:spawnZonesState', onState);
    window.addEventListener('skymp5-client:spawnMobResults', onMobs);
    window.addEventListener('skymp5-client:adminDungeonList', onDungeons);
    send({ action: 'spawnZonesRequest' });
    send({ action: 'adminDungeonList' });
    return () => {
      window.removeEventListener('skymp5-client:spawnZonesState', onState);
      window.removeEventListener('skymp5-client:spawnMobResults', onMobs);
      window.removeEventListener('skymp5-client:adminDungeonList', onDungeons);
    };
  }, []);

  const zone = useMemo(
    () => (state && selected && selected !== 'new' ? state.zones.find((z) => z.id === selected) ?? null : null),
    [state, selected]
  );

  // The draft follows the server's version until the admin starts editing
  const dirty = !!(zone && draft && !sameDraft(draft, toDraft(zone)));
  useEffect(() => {
    if (zone && !dirty) setDraft(toDraft(zone));
    if (!zone && selected !== 'new') setDraft(null);
  }, [zone, dirty, selected]);

  useEffect(() => {
    setConfirmDelete(false);
    setPicking(false);
  }, [selected]);

  // The catalog of creatures is searched on the server, after a short pause while typing, only while the picker
  // is open. It is built in the background at server start: ask again until it is ready.
  useEffect(() => {
    if (!picking) return undefined;
    const t = window.setTimeout(
      () => send({ action: 'spawnMobSearch', query: mobQuery, category: mobCategory }),
      mobs ? 250 : 0
    );
    return () => window.clearTimeout(t);
  }, [picking, mobQuery, mobCategory]);
  useEffect(() => {
    if (!picking || !mobs || mobs.ready) return undefined;
    const t = window.setTimeout(() => send({ action: 'spawnMobSearch', query: mobQuery, category: mobCategory }), 2000);
    return () => window.clearTimeout(t);
  }, [picking, mobs]);

  const choose = (id: string | 'new'): void => {
    setSelected(id);
    setDraft(null);
  };

  const patch = (changes: Partial<Draft>): void => setDraft((d) => (d ? { ...d, ...changes } : d));

  const usedIds = new Set((draft?.entries ?? []).map((e) => e.formId));
  const limits = state?.limits;
  const categoryLabel = (id: string): string => state?.categories.find((c) => c.id === id)?.label ?? id;

  const save = (): void => {
    if (!zone || !draft) return;
    send({ action: 'spawnZoneSave', id: zone.id, ...draft });
  };

  const renderList = (): React.ReactNode => (
    <div className="spawn-tab__list">
      <button
        type="button"
        className={'admin-panel__button admin-panel__button--primary spawn-tab__new'}
        onClick={() => choose('new')}
      >
        Nouvelle zone
      </button>
      {!state ? (
        <div className="admin-panel__empty">Chargement...</div>
      ) : state.zones.length === 0 ? (
        <div className="admin-panel__empty">Aucune zone. Crée la première.</div>
      ) : (
        state.zones.map((z) => (
          <div
            key={z.id}
            className={'spawn-tab__zone' + (selected === z.id ? ' spawn-tab__zone--selected' : '')}
            onClick={() => choose(z.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter') choose(z.id);
            }}
          >
            <div className="spawn-tab__zone-head">
              <span className={'admin-panel__dot' + (z.enabled ? ' admin-panel__dot--online' : ' admin-panel__dot--offline')} />
              <span className="spawn-tab__zone-name">{z.name}</span>
            </div>
            <div className="spawn-tab__zone-meta">
              {z.alive}/{z.maxAlive} en vie
              {z.waiting > 0 ? ` - ${z.waiting} en attente` : ''}
            </div>
            <div className="spawn-tab__zone-meta">
              {z.shape === 'circle' ? `Cercle, rayon ${z.radiusMeters} m` : `Contour, ${z.vertexCount} sommets`} - {z.areaKm2} km2
            </div>
            {z.pointsTotal === 0 ? <div className="spawn-tab__zone-warn">Aucun point : rien n'apparaît</div> : null}
          </div>
        ))
      )}
    </div>
  );

  const renderNew = (): React.ReactNode => (
    <div className="spawn-tab__editor">
      <div className="spawn-tab__section">
        <div className="spawn-tab__title">Nouvelle zone</div>
        <div className="spawn-tab__hint">
          Place-toi où la zone doit commencer. Donne-lui un nom, puis choisis une forme. Tu pourras tout régler ensuite :
          les créatures, leur nombre, le délai de réapparition, l'annonce faite au joueur.
        </div>
        <label className="spawn-tab__field">
          <span className="spawn-tab__label">Nom de la zone</span>
          <input
            className="admin-panel__search"
            placeholder="Plaines de Blancherive"
            value={newName}
            maxLength={limits?.maxNameLength ?? 40}
            onChange={(e) => setNewName(e.target.value)}
          />
        </label>

        <div className="spawn-tab__choice">
          <div className="spawn-tab__choice-card">
            <div className="spawn-tab__choice-title">Cercle autour de moi</div>
            <div className="spawn-tab__choice-text">Le plus rapide : la zone est centrée sur ta position.</div>
            <label className="spawn-tab__inline">
              Rayon
              <input
                className="spawn-tab__num"
                type="number"
                min={limits?.minCircleMeters ?? 22}
                max={5000}
                value={newRadius}
                onChange={(e) => setNewRadius(clampNum(e.target.value, 0, 5000, 300))}
              />
              m
            </label>
            <button
              type="button"
              className="admin-panel__button admin-panel__button--primary"
              disabled={!newName.trim() || newRadius < (limits?.minCircleMeters ?? 22)}
              onClick={() => {
                send({ action: 'spawnZoneCreateCircle', name: newName.trim(), radiusMeters: newRadius });
                setNewName('');
                setSelected(null);
              }}
            >
              Créer le cercle
            </button>
          </div>

          <div className="spawn-tab__choice-card">
            <div className="spawn-tab__choice-title">Tracer le contour à pied</div>
            <div className="spawn-tab__choice-text">
              Pour une forme libre. Le panel se ferme : marche le long du contour, G pose un sommet, Retour arrière
              reprend le dernier, Entrée termine. Il faut 3 sommets.
            </div>
            <button
              type="button"
              className="admin-panel__button admin-panel__button--primary"
              disabled={!newName.trim()}
              onClick={() => {
                send({ action: 'spawnZoneDrawStart', name: newName.trim() });
                setNewName('');
                setSelected(null);
              }}
            >
              Commencer le tracé
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderEditor = (z: ZoneInfo, d: Draft): React.ReactNode => (
    <div className="spawn-tab__editor">
      <div className="spawn-tab__bar">
        <input
          className="spawn-tab__name"
          value={d.name}
          maxLength={limits?.maxNameLength ?? 40}
          onChange={(e) => patch({ name: e.target.value })}
          aria-label="Nom de la zone"
        />
        <label className="admin-panel__online-toggle">
          <input type="checkbox" checked={d.enabled} onChange={(e) => patch({ enabled: e.target.checked })} />
          Active
        </label>
        <button type="button" className="admin-panel__button admin-panel__button--primary" disabled={!dirty} onClick={save}>
          {dirty ? 'Enregistrer' : 'Enregistré'}
        </button>
      </div>

      <div className="spawn-tab__section">
        <div className="spawn-tab__title">Créatures de la zone</div>
        {d.entries.length === 0 ? (
          <div className="spawn-tab__warn">Aucune créature choisie : la zone ne fera rien. Ajoutes-en une ci-dessous.</div>
        ) : (
          <div className="spawn-tab__entries">
            <div className="spawn-tab__entry spawn-tab__entry--head">
              <span>Créature</span>
              <span>Fréquence</span>
              <span>Groupe de</span>
              <span>à</span>
              <span>Attaque</span>
              <span />
            </div>
            {d.entries.map((en, i) => (
              <div key={en.formId} className="spawn-tab__entry">
                <span className="spawn-tab__species">{en.name}</span>
                <select
                  className="spawn-tab__select"
                  value={en.weight}
                  onChange={(e) => {
                    const entries = d.entries.slice();
                    entries[i] = { ...en, weight: Number(e.target.value) };
                    patch({ entries });
                  }}
                >
                  {!WEIGHTS.some((w) => w.value === en.weight) ? <option value={en.weight}>Poids {en.weight}</option> : null}
                  {WEIGHTS.map((w) => (
                    <option key={w.value} value={w.value}>{w.label}</option>
                  ))}
                </select>
                <input
                  className="spawn-tab__num"
                  type="number"
                  min={1}
                  max={10}
                  value={en.minGroup}
                  onChange={(e) => {
                    const entries = d.entries.slice();
                    const minGroup = clampNum(e.target.value, 1, 10, 1);
                    entries[i] = { ...en, minGroup, maxGroup: Math.max(en.maxGroup, minGroup) };
                    patch({ entries });
                  }}
                />
                <input
                  className="spawn-tab__num"
                  type="number"
                  min={en.minGroup}
                  max={10}
                  value={en.maxGroup}
                  onChange={(e) => {
                    const entries = d.entries.slice();
                    entries[i] = { ...en, maxGroup: clampNum(e.target.value, en.minGroup, 10, en.minGroup) };
                    patch({ entries });
                  }}
                />
                <input
                  type="checkbox"
                  className="spawn-tab__check"
                  checked={en.hostile}
                  title="Attaque les joueurs à vue. Décoche pour un animal paisible."
                  onChange={(e) => {
                    const entries = d.entries.slice();
                    entries[i] = { ...en, hostile: e.target.checked };
                    patch({ entries });
                  }}
                />
                <button
                  type="button"
                  className="admin-panel__button admin-panel__button--secondary"
                  title="Retirer cette créature"
                  onClick={() => patch({ entries: d.entries.filter((_, j) => j !== i) })}
                >
                  Retirer
                </button>
              </div>
            ))}
          </div>
        )}
        {d.entries.length < (limits?.maxEntries ?? 30) ? (
          <button
            type="button"
            className="admin-panel__button spawn-tab__add"
            onClick={() => setPicking((p) => !p)}
          >
            {picking ? 'Fermer la recherche' : 'Ajouter une créature'}
          </button>
        ) : null}
        {picking ? (
          <div className="spawn-tab__picker">
            <div className="spawn-tab__hint">
              Tout ce que contiennent les fichiers du jeu et des mods : animaux, créatures, morts-vivants, dragons,
              daedra, dwemer, humanoïdes (bandits, gardes...). Les personnages nommés ne sont pas proposés.
            </div>
            <input
              className="admin-panel__search"
              placeholder="Rechercher (nom ou identifiant : loup, draugr, troll, bandit...)"
              value={mobQuery}
              maxLength={60}
              onChange={(e) => setMobQuery(e.target.value)}
            />
            <div className="admin-panel__chips">
              {(state?.categories ?? []).map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={'admin-panel__chip' + (mobCategory === c.id ? ' admin-panel__chip--active' : '')}
                  onClick={() => setMobCategory(c.id)}
                >
                  {c.label}{mobs && mobs.ready ? ` (${mobs.counts[c.id] || 0})` : ''}
                </button>
              ))}
            </div>
            <div className="spawn-tab__results">
              {!mobs ? (
                <div className="admin-panel__empty">Chargement...</div>
              ) : !mobs.ready ? (
                <div className="admin-panel__empty">Le catalogue se construit au démarrage du serveur, quelques secondes...</div>
              ) : mobs.results.length === 0 ? (
                <div className="admin-panel__empty">Aucune créature ne correspond.</div>
              ) : (
                <>
                  {mobs.results.map((m) => (
                    <div key={m.id} className="spawn-tab__result">
                      <div className="spawn-tab__result-text">
                        <div className="spawn-tab__result-name">{m.n}</div>
                        <div className="spawn-tab__result-meta">
                          <span className="admin-panel__badge">{categoryLabel(m.c)}</span> {m.edid !== m.n ? `${m.edid} - ` : ''}{m.mod}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="admin-panel__button admin-panel__button--primary"
                        disabled={usedIds.has(m.id)}
                        onClick={() =>
                          patch({
                            entries: [
                              ...d.entries,
                              { formId: m.id, name: m.n, weight: 3, minGroup: 1, maxGroup: 2, hostile: m.c !== 'animal' },
                            ],
                          })
                        }
                      >
                        {usedIds.has(m.id) ? 'Ajoutée' : 'Ajouter'}
                      </button>
                    </div>
                  ))}
                  {mobs.total > mobs.results.length ? (
                    <div className="admin-panel__empty">
                      {mobs.results.length} affichées sur {mobs.total} : précise la recherche pour voir les autres.
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </div>
        ) : null}
      </div>

      <div className="spawn-tab__section">
        <div className="spawn-tab__title">Population et réapparition</div>
        <div className="spawn-tab__row">
          <label className="spawn-tab__field">
            <span className="spawn-tab__label">Créatures en même temps dans la zone</span>
            <input
              className="spawn-tab__num"
              type="number"
              min={1}
              max={limits?.maxAlive ?? 40}
              value={d.maxAlive}
              onChange={(e) => patch({ maxAlive: clampNum(e.target.value, 1, limits?.maxAlive ?? 40, d.maxAlive) })}
            />
          </label>
          <label className="spawn-tab__field">
            <span className="spawn-tab__label">Un groupe tué revient après (minutes)</span>
            <input
              className="spawn-tab__num"
              type="number"
              min={1}
              max={limits?.maxRespawnMinutes ?? 10080}
              value={d.respawnMinutes}
              onChange={(e) =>
                patch({ respawnMinutes: clampNum(e.target.value, 1, limits?.maxRespawnMinutes ?? 10080, d.respawnMinutes) })
              }
            />
          </label>
        </div>
        <div className="admin-panel__chips">
          {RESPAWN_PRESETS.map((m) => (
            <button
              key={m}
              type="button"
              className={'admin-panel__chip' + (d.respawnMinutes === m ? ' admin-panel__chip--active' : '')}
              onClick={() => patch({ respawnMinutes: m })}
            >
              {humanMinutes(m)}
            </button>
          ))}
        </div>
        <div className="spawn-tab__hint">
          Un groupe tué reste mort {humanMinutes(d.respawnMinutes)}, même si les joueurs s'éloignent. Un délai raccourci
          s'applique aussi aux groupes déjà en attente.
        </div>
      </div>

      <div className="spawn-tab__section">
        <div className="spawn-tab__title">Annonce au joueur</div>
        <label className="admin-panel__online-toggle">
          <input type="checkbox" checked={d.announce} onChange={(e) => patch({ announce: e.target.checked })} />
          Prévenir le joueur quand il entre dans la zone
        </label>
        {d.announce ? (
          <>
            <input
              className="admin-panel__search"
              value={d.announceText}
              maxLength={limits?.maxAnnounceLength ?? 120}
              placeholder={state?.defaultAnnounceText}
              onChange={(e) => patch({ announceText: e.target.value })}
            />
            <div className="spawn-tab__hint">
              Écris {'{nom}'} pour insérer le nom de la zone. Aperçu :{' '}
              <span className="spawn-tab__preview">
                {(d.announceText.trim() || state?.defaultAnnounceText || '').replace(/\{nom\}/gi, d.name)}
              </span>
            </div>
          </>
        ) : null}
      </div>

      <div className="spawn-tab__section">
        <div className="spawn-tab__title">Points d'apparition</div>
        <div className="spawn-tab__hint">
          Les créatures naissent à des endroits fixes, jamais au hasard : les points du jeu (là où les créateurs de Skyrim
          avaient placé des animaux) et ceux que tu poses toi-même.
        </div>
        <div className="spawn-tab__stats">
          <span>{z.pointsGame} points du jeu</span>
          <span>{z.pointsManual} posés à la main</span>
          <span>{z.pointsTotal} au total</span>
        </div>
        {z.pointsTotal === 0 ? (
          <div className="spawn-tab__warn">
            Aucun point dans cette zone : rien n'y apparaîtra. Active les points du jeu ou pose-en toi-même.
          </div>
        ) : null}
        <label className="admin-panel__online-toggle">
          <input type="checkbox" checked={d.useVanillaPoints} onChange={(e) => patch({ useVanillaPoints: e.target.checked })} />
          Utiliser les points du jeu dans la zone
        </label>
        <div className="spawn-tab__buttons">
          <button
            type="button"
            className="admin-panel__button"
            title="Ajoute un point là où tu te tiens (tu dois être dans la zone)"
            onClick={() => send({ action: 'spawnZoneAddPoint', id: z.id })}
          >
            Ajouter un point ici
          </button>
          {z.pointsManual > 0 ? (
            <button type="button" className="admin-panel__button admin-panel__button--secondary" onClick={() => send({ action: 'spawnZoneClearPoints', id: z.id })}>
              Retirer tous mes points
            </button>
          ) : null}
        </div>
        {z.manualPoints.length > 0 ? (
          <div className="spawn-tab__points">
            {z.manualPoints.map((p, i) => (
              <div key={`${p[0]},${p[1]}`} className="spawn-tab__point">
                <span>[{p[0]}, {p[1]}, {p[2]}]</span>
                <button
                  type="button"
                  className="admin-panel__button admin-panel__button--secondary"
                  onClick={() => send({ action: 'spawnZoneRemovePoint', id: z.id, index: i })}
                >
                  Retirer
                </button>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      <div className="spawn-tab__section">
        <div className="spawn-tab__title">Contour et outils</div>
        <div className="spawn-tab__stats">
          <span>{z.alive}/{z.maxAlive} créatures en vie</span>
          <span>{z.waiting} groupe(s) en attente de réapparition</span>
        </div>
        <div className="spawn-tab__buttons">
          <button type="button" className="admin-panel__button" onClick={() => send({ action: 'spawnZonePreview', id: z.id })}>
            Voir le contour
          </button>
          <button type="button" className="admin-panel__button" onClick={() => send({ action: 'spawnZoneGoto', id: z.id })}>
            Y aller
          </button>
          <button
            type="button"
            className="admin-panel__button"
            title="Le panel se ferme : marche le long du nouveau contour, G pose un sommet, Entrée termine"
            onClick={() => send({ action: 'spawnZoneDrawStart', id: z.id })}
          >
            Refaire le contour
          </button>
          <button
            type="button"
            className="admin-panel__button"
            title="Retire les créatures de la zone et oublie tous les délais de réapparition"
            onClick={() => send({ action: 'spawnZoneReset', id: z.id })}
          >
            Réinitialiser
          </button>
          {confirmDelete ? (
            <>
              <button
                type="button"
                className="admin-panel__button admin-panel__button--danger"
                onClick={() => {
                  send({ action: 'spawnZoneDelete', id: z.id });
                  setSelected(null);
                }}
              >
                Confirmer la suppression
              </button>
              <button type="button" className="admin-panel__button admin-panel__button--secondary" onClick={() => setConfirmDelete(false)}>
                Annuler
              </button>
            </>
          ) : (
            <button type="button" className="admin-panel__button admin-panel__button--danger" onClick={() => setConfirmDelete(true)}>
              Supprimer la zone
            </button>
          )}
        </div>
      </div>
    </div>
  );

  const renderZones = (): React.ReactNode => (
    <div className="spawn-tab__split">
      {renderList()}
      {selected === 'new' ? (
        renderNew()
      ) : zone && draft ? (
        renderEditor(zone, draft)
      ) : (
        <div className="spawn-tab__editor">
          <div className="admin-panel__empty">
            Choisis une zone à gauche, ou crée-en une. {state ? `${state.totalAlive} créature(s) en vie sur le serveur.` : ''}
          </div>
        </div>
      )}
    </div>
  );

  const renderDungeons = (): React.ReactNode => (
    <div className="spawn-tab__dungeons">
      <div className="spawn-tab__hint">
        Les donjons gardent leur propre système (une partie partagée, remise à zéro après un délai). Tu règles ici leur
        durée et leur réinitialisation. Le butin des coffres se règle dans l'éditeur complet.
      </div>
      <div className="spawn-tab__buttons">
        <button type="button" className="admin-panel__button" onClick={() => send({ action: 'adminDungeonManager' })}>
          Ouvrir l'éditeur complet (butin)
        </button>
      </div>
      {!dungeons ? (
        <div className="admin-panel__empty">Chargement...</div>
      ) : dungeons.length === 0 ? (
        <div className="admin-panel__empty">Aucun donjon configuré.</div>
      ) : (
        dungeons.map((dg) => <DungeonCard key={dg.id} dungeon={dg} />)
      )}
    </div>
  );

  return (
    <div className="admin-panel__body">
      <div className="admin-panel__chips">
        <button
          type="button"
          className={'admin-panel__chip' + (view === 'zones' ? ' admin-panel__chip--active' : '')}
          onClick={() => setView('zones')}
        >
          Zones de spawn{state ? ` (${state.zones.length})` : ''}
        </button>
        <button
          type="button"
          className={'admin-panel__chip' + (view === 'dungeons' ? ' admin-panel__chip--active' : '')}
          onClick={() => {
            setView('dungeons');
            send({ action: 'adminDungeonList' });
          }}
        >
          Donjons{dungeons ? ` (${dungeons.length})` : ''}
        </button>
      </div>
      {view === 'zones' ? renderZones() : renderDungeons()}
    </div>
  );
};

const DungeonCard: React.FC<{ dungeon: DungeonInfo }> = ({ dungeon }) => {
  const [name, setName] = useState(dungeon.name);
  const [session, setSession] = useState(dungeon.sessionTimeLimitMinutes);
  const [reset, setReset] = useState(dungeon.resetDelayMinutes);

  useEffect(() => {
    setName(dungeon.name);
    setSession(dungeon.sessionTimeLimitMinutes);
    setReset(dungeon.resetDelayMinutes);
  }, [dungeon]);

  const changed =
    name.trim() !== dungeon.name ||
    session !== dungeon.sessionTimeLimitMinutes ||
    reset !== dungeon.resetDelayMinutes;

  const saveDungeon = (): void => {
    if (name.trim() && name.trim() !== dungeon.name) {
      send({ action: 'adminDungeonRename', dungeonId: dungeon.id, name: name.trim(), embedded: true });
    }
    if (session !== dungeon.sessionTimeLimitMinutes || reset !== dungeon.resetDelayMinutes) {
      send({
        action: 'adminDungeonTimers',
        dungeonId: dungeon.id,
        sessionTimeLimitMinutes: session,
        resetDelayMinutes: reset,
        embedded: true,
      });
    }
  };

  return (
    <div className="spawn-tab__section spawn-tab__dungeon">
      <div className="spawn-tab__bar">
        <input
          className="spawn-tab__name"
          value={name}
          maxLength={60}
          onChange={(e) => setName(e.target.value)}
          aria-label="Nom du donjon"
        />
        <span className="admin-panel__badge">{PHASE_LABEL[dungeon.phase] ?? dungeon.phase}</span>
        <button type="button" className="admin-panel__button admin-panel__button--primary" disabled={!changed} onClick={saveDungeon}>
          {changed ? 'Enregistrer' : 'Enregistré'}
        </button>
      </div>
      <div className="spawn-tab__row">
        <label className="spawn-tab__field">
          <span className="spawn-tab__label">Durée maximale d'une partie (minutes)</span>
          <input
            className="spawn-tab__num"
            type="number"
            min={1}
            max={1440}
            value={session}
            onChange={(e) => setSession(clampNum(e.target.value, 1, 1440, session))}
          />
        </label>
        <label className="spawn-tab__field">
          <span className="spawn-tab__label">Remise à zéro après (minutes)</span>
          <input
            className="spawn-tab__num"
            type="number"
            min={1}
            max={1440}
            value={reset}
            onChange={(e) => setReset(clampNum(e.target.value, 1, 1440, reset))}
          />
        </label>
      </div>
      <div className="spawn-tab__stats">
        <span>{dungeon.cellOrWorldDesc.length} lieu(x)</span>
        <span>{dungeon.zonesCount} zone(s) de réglage</span>
        <span>{dungeon.excludedCount} monstre(s) exclu(s)</span>
        <span>{dungeon.lootContainers.length} coffre(s) avec butin</span>
      </div>
    </div>
  );
};
